import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'

import ColorSelector from 'shared/components/ColorSelector'
import ThirdLevelTitle from 'shared/components/Tools/components/LeftPanel/components/ThirdLevelTitle'
import SecondLevelTitle from 'shared/components/Tools/components/PanelSecondLevelTitle'
import CMYK from 'shared/entities/CMYK'
import { CMYKValues, ColorCMYK } from 'shared/types/global'
import Flex from 'shared/ui/Flex'
import CustomScrollbars from 'shared/ui/SimpleBar'

const Color = styled.div<{ value?: string; isActive?: boolean }>`
  background-color: ${({ value }) => value};
  border-radius: 5px;
  cursor: pointer;
  width: 22px;
  height: 22px;

  ${({ isActive, theme, value }) =>
    isActive &&
    css`
      box-shadow: 0px 0px 0px 2px ${theme.color.base.c0},
        0px 0px 0px 4px ${value};
    `}
`

const OtherColors = styled.div`
  width: 100%;
  justify-content: end;
  display: grid;
  grid-template-columns: repeat(4, 27px);
  grid-template-rows: repeat(2, 27px);
  margin-left: 10px;
`

const CurrentColor = styled(Color)`
  box-shadow: 0px 3px 3px rgba(0, 0, 0, 0.05);
  width: 48px;
  height: 48px;
`

const Label = styled.span`
  width: 100%;
  text-align: center;
  font-size: 10px;
  color: ${({ theme }) => theme.color.base.c6};
`

const ColorSelectorPanelCurrentColor = styled.div`
  width: 90%;
  height: 57px;
  border-radius: 15px;
  margin-top: 24px;
`

const otherColors = [
  new CMYK({ c: 0, m: 100, y: 100, k: 37 }),
  new CMYK({ c: 64, m: 37, y: 0, k: 37 }),
  new CMYK({ c: 53, m: 72, y: 0, k: 8 }),
  new CMYK({ c: 100, m: 12, y: 50, k: 23 }),

  new CMYK({ c: 0, m: 45, y: 76, k: 0 }),
  new CMYK({ c: 0, m: 0, y: 0, k: 90 }),
  new CMYK({ c: 0, m: 26, y: 100, k: 0 }),
  new CMYK({ c: 100, m: 0, y: 7, k: 28 })
]

type ColorsSetProps = {
  labelId: string
  currentColor: CMYK
  onCurrentClick: (color: ColorCMYK) => void
  onOtherClick: (color: ColorCMYK) => void
}

const CoverStartEditingPanelColorsSet: React.FC<ColorsSetProps> = ({
  labelId,
  currentColor,
  onCurrentClick,
  onOtherClick
}) => {
  const { formatMessage } = useIntl()
  const [isColorSelectorOpen, setIsColorSelectorOpen] =
    useState(false)
  const [selectedColor, setSelectedColor] = useState<ColorCMYK>({
    c: currentColor.c,
    m: currentColor.m,
    y: currentColor.y,
    k: currentColor.k
  })

  const handleCurrentColorClick = () => {
    setIsColorSelectorOpen(true)
    setSelectedColor({
      c: currentColor.c,
      m: currentColor.m,
      y: currentColor.y,
      k: currentColor.k
    })
  }

  const handleColorChange = (field: CMYKValues, value: number) => {
    const newColor = { ...selectedColor, [field]: value }
    setSelectedColor(newColor)
    onCurrentClick(newColor)
  }

  const handleBackButtonClick = () => {
    setIsColorSelectorOpen(false)
  }

  useEffect(() => {
    if (!isColorSelectorOpen) {
      setSelectedColor({
        c: currentColor.c,
        m: currentColor.m,
        y: currentColor.y,
        k: currentColor.k
      })
    }
  }, [currentColor, isColorSelectorOpen])

  if (isColorSelectorOpen) {
    return (
      <CustomScrollbars
        fixedHeaderMarginBottom={180}
        fixedHeader={
          <>
            <SecondLevelTitle
              titleId="Change.color"
              onClick={handleBackButtonClick}
              margin={false}
            />
            <ColorSelectorPanelCurrentColor
              style={{
                background: new CMYK({ ...selectedColor }).toCSS()
              }}
            />
          </>
        }
      >
        <div>
          <ThirdLevelTitle titleId="Change.color.selector" />
          <ColorSelector
            value={selectedColor}
            onChange={handleColorChange}
          />
        </div>
      </CustomScrollbars>
    )
  }

  return (
    <Flex columnGap={8}>
      <Flex direction="column" rowGap={8}>
        <CurrentColor
          style={{ background: currentColor.toCSS() }}
          onClick={handleCurrentColorClick}
        />

        <Label>{formatMessage({ id: labelId })}</Label>
      </Flex>

      <OtherColors>
        {otherColors.map((color, i) => (
          <Color
            key={i}
            value={color.toCSS()}
            isActive={color.isEqual(currentColor)}
            onClick={() => onOtherClick(color)}
          />
        ))}
      </OtherColors>
    </Flex>
  )
}

export default CoverStartEditingPanelColorsSet
